import {
  Component,
  OnInit,
  ChangeDetectionStrategy,
  Input,
  EventEmitter,
  Output,
  ChangeDetectorRef,
  OnDestroy,
} from '@angular/core';
import { SafeHtml } from '@angular/platform-browser';
import { DayBlockService } from '@app/infrastructure/core/services/api/day-block.service';
import { KaraokePlayerService } from '@app/infrastructure/core/services/karaoke-player.service';
import { QuizAnswerState } from '@app/infrastructure/core/services/state/quiz-answer-state';
import { SelectedGradeStateService } from '@app/infrastructure/core/services/state/selected-grade-state.service';
import { UserStateService } from '@app/infrastructure/core/services/state/user-state.service';
import {
  IStudentAnswerDTO,
  StudentAnswerDTO,
} from '@app/infrastructure/models/answer';
import { DayBlockDTO } from '@app/infrastructure/models/day-block';
import { GradeUnitDayDTO } from '@app/infrastructure/models/grade-unit/grade-unit-day';
import {
  FormattedKaraokePlayer,
  IKaraokeDTO,
  KaraokeDTO,
} from '@app/infrastructure/models/grade-unit/karaoke';
import { ImageBlockDTO } from '@app/infrastructure/models/image-block';
import {
  IQuizBlockDTO,
  QuizBlockDTO,
  QuizTypes,
} from '@app/infrastructure/models/quiz-block';
import { RoleEnum } from '@app/infrastructure/models/role';
import { StudentAssignmentDTO } from '@app/infrastructure/models/student-assignment';
import { VideoEmbedBlock } from '@app/infrastructure/models/video-embed-block';
import { BLOCK_TYPES } from '@app/infrastructure/utils/constants';
import { VideoEmbedUtils } from '@app/infrastructure/utils/video-embed.utils';

@Component({
  selector: 'app-block-viewer',
  templateUrl: './block-viewer.component.html',
  styleUrls: ['./block-viewer.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BlockViewerComponent implements OnInit, OnDestroy {
  @Input() block: DayBlockDTO;
  @Input() isActive: boolean;
  @Input() studentAssignment: StudentAssignmentDTO;
  @Input() studentId: string = '';
  @Input() answers: IStudentAnswerDTO[] = [];
  @Input() quizAnswer: IStudentAnswerDTO;
  @Input() isWrong: boolean = false;
  @Input() isCorrect: boolean = false;
  @Input() unansweredQuizBlockIds: number[];
  @Input() gradeLevel: number;
  @Input() unanswered: boolean = false;
  @Input() correctAnswer: string = '';
  @Input() quizCompleted: boolean = false;
  @Input() freeResponseScore: number = 0;
  @Input() isFreeAnswer: boolean = false;
  @Input() gradeUnitDay: GradeUnitDayDTO;
  public karaokePlayer: FormattedKaraokePlayer = new FormattedKaraokePlayer();
  public videoHtml: SafeHtml;
  public fileLocation: string;
  public quiz: IQuizBlockDTO = new QuizBlockDTO();
  public BLOCKTYPES = BLOCK_TYPES;
  public isStudent: boolean = false;
  public result: IStudentAnswerDTO = null;
  public isFreeAnswerPending: boolean = false;
  @Output() emitSubmitAnswer = new EventEmitter<boolean>();
  @Output() emitResults = new EventEmitter<IStudentAnswerDTO>();
  private intervals = [];

  /**
   * For now, get the first video or image and first karaoke block
   */
  constructor(
    private readonly karaokeService: KaraokePlayerService,
    private readonly dayBlockService: DayBlockService,
    private readonly videoEmbedUtils: VideoEmbedUtils,
    private userStateService: UserStateService,
    private quizAnswerState: QuizAnswerState,
    private cd: ChangeDetectorRef,
    private gradeState: SelectedGradeStateService,
  ) {
    this.gradeLevel = this.gradeState.gradeId;
    this.gradeState.currentGrade$.subscribe();
  }

  ngOnInit(): void {
    this.quizAnswerState.emitUnansweredBlockIds.subscribe((list) => {
      this.setList(list);
    });
    this.quizAnswerState.emitSubmitAnswers.subscribe((value) =>
      this.showResult(value),
    );

    if (this.answers?.length) {
      this.quizAnswer = this.answers?.filter((answer: IStudentAnswerDTO) => {
        return answer?.dayBlockId === this.block?.id;
      })[0];
      if (this.quizCompleted) this.showResult(true);
    }

    this.userStateService.getUserSession().subscribe((user) => {
      if (user?.role?.roleName === RoleEnum.STUDENT) {
        this.isStudent = true;
        this.studentId = user.student.id;
      }
    });
    switch (this.block?.blockType) {
      case BLOCK_TYPES.VideoEmbedBlock:
        this.videoHtml = this.getVideoHtml(this.block as VideoEmbedBlock);
        break;
      case BLOCK_TYPES.KaraokeBlock:
        this.getKaraokePlayer();
        break;
      case BLOCK_TYPES.ImageBlock:
        const tempBlock = this.block as ImageBlockDTO;
        this.fileLocation = tempBlock.file.signedUrl;
        break;
      case BLOCK_TYPES.QuizBlock:
        this.quiz = this.block as IQuizBlockDTO;
        break;
      default:
        break;
    }
  }

  ngOnDestroy(): void {
    this.intervals.forEach((interval) => clearInterval(interval));
  }

  public answerSaved(value: boolean) {
    this.unanswered = value;
  }

  public showResult(value: boolean) {
    if (!this.studentAssignment?.completedAt) return;
    const studentAnswer = this.result || this.quizAnswer;
    if (value && studentAnswer) {
      this.unanswered = false;
      switch (this.block.quizType) {
        case QuizTypes.MULTIPLECHOICE:
          const correctAnswers = this.block.options.filter(
            (option) => `${option.correct}` === 'true',
          );
          const correctStudentAnswers = correctAnswers.filter(
            (option) => studentAnswer.studentOptionsAnswer === option.answer,
          );
          this.isWrong = !(correctStudentAnswers.length > 0);
          if (this.isWrong) this.correctAnswer = correctAnswers[0]?.answer;
          break;
        case QuizTypes.BOOLEANCHOICE:
          this.isWrong =
            this.block.trueOrFalse !== studentAnswer.studentTrueOrFalseAnswer;
          if (this.isWrong) this.correctAnswer = `${this.block.trueOrFalse}`;
          break;
        case QuizTypes.FREEANSWER:
          this.isFreeAnswer = true;
          if (this.quizAnswer?.score?.finalScore) {
            this.freeResponseScore = this.quizAnswer?.score?.finalScore;
            if (this.freeResponseScore > 0) this.isCorrect = true;
            this.isFreeAnswerPending = false;
          } else {
            this.isFreeAnswerPending = true;
          }
          break;
        default:
          break;
      }
      // TODO: remove the if statement once the above TODO is addressed
      if (this.block.quizType !== QuizTypes.FREEANSWER)
        this.isCorrect = !this.isWrong;
      if (this.result) this.cd.detectChanges();
    }
  }

  public setResult(result: IStudentAnswerDTO): void {
    this.result = result;
    if (this.block.quizType !== QuizTypes.FREEANSWER) {
      switch (this.block.quizType) {
        case QuizTypes.MULTIPLECHOICE:
          if (!result.studentAssignmentId) {
            const correctAnswer = this.block.options.filter(
              (option) =>
                option.correct === 'true' &&
                option.answer === result.studentOptionsAnswer,
            );
            if (correctAnswer.length) {
              this.isCorrect = true;
              this.isWrong = false;
              this.cd.detectChanges();
            } else {
              this.isWrong = true;
              this.isCorrect = false;
              this.cd.detectChanges();
            }
            sessionStorage.clear();
          } else {
            const studentCorrectAnswers = this.block.options.filter(
              (option) =>
                option.correct && option.answer === result.studentOptionsAnswer,
            );
            if (studentCorrectAnswers.length) {
              this.quizAnswerState.addToAssignmentPoints();
            }
          }
          break;
        case QuizTypes.BOOLEANCHOICE:
          if (!result.studentAssignmentId) {
            if (
              this.block.trueOrFalse.toString() ===
              result.studentTrueOrFalseAnswer
            ) {
              this.isCorrect = true;
              this.isWrong = false;
              this.cd.detectChanges();
            } else {
              this.isWrong = true;
              this.isCorrect = false;
              this.cd.detectChanges();
            }
            sessionStorage.clear();
          } else {
            if (this.block.trueOrFalse === result.studentTrueOrFalseAnswer) {
              this.quizAnswerState.addToAssignmentPoints();
            }
          }
          break;
        default:
          break;
      }
    }
  }
  setList(list: { id: number; hasAnswer: boolean }[]) {
    if (list.some((block) => block.id === this.block?.id)) {
      this.unanswered = true;
    }
    this.cd.detectChanges();
  }

  getVideoHtml(block: VideoEmbedBlock) {
    return this.videoEmbedUtils.getVideoEmbedFromUrl(block.url);
  }

  getKaraokePlayer() {
    const karaoke: IKaraokeDTO = new KaraokeDTO();
    Object.assign(karaoke, this.block);
    if (!karaoke.originalTranscription) {
      const pollingInterval = window.setInterval(() => {
        this.dayBlockService
          .getDayBlockById(this.gradeUnitDay.id, this.block.id)
          .subscribe((data) => {
            const karaokeData = data as IKaraokeDTO;
            if (karaokeData.originalTranscription) {
              Object.assign(karaoke, { ...karaokeData });
              this.karaokePlayer = this.karaokeService.mapToFormattedPlayer(
                this.karaokeService.mapKaraokeDTOToKaraokePlayer(karaoke),
              );
              this.cd.detectChanges();
              clearInterval(pollingInterval);
              this.intervals.pop();
            }
          });
      }, 5000);
      this.intervals.push(pollingInterval);
    } else {
      this.karaokePlayer = this.karaokeService.mapToFormattedPlayer(
        this.karaokeService.mapKaraokeDTOToKaraokePlayer(karaoke),
      );
    }
  }
}
